// src/pages/Login.jsx
import { useState, useEffect, useRef } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { GoogleOAuthProvider, GoogleLogin } from '@react-oauth/google'
import { useAuth } from '../context/AuthContext'
import { login, googleLogin, getProfile } from '../api/services'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID

// -- GMC-Branded Google Icon (dark theme version) --------------------------
function GoogleIcon() {
    return (
        <svg width="20" height="20" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M24 9.5C27.6 9.5 30.8 10.8 33.3 13.1L40.5 6.1C36.2 2.3 30.5 0 24 0C14.8 0 7.1 4.8 3.3 11.8L9.9 17.1C12.3 12.5 17.5 9.5 24 9.5Z" fill="#EA4335" />
            <path d="M46.5 24.5C46.5 22.9 46.4 21.4 46.2 20H24V29.4H36.7C35.9 33.8 33.6 37.7 30.1 40.3L36.7 45.7C43.3 40.3 46.5 33.1 46.5 24.5Z" fill="#4285F4" />
            <path d="M9.9 30.9C9.1 29.1 8.6 27.2 8.6 25C8.6 22.8 9.1 20.9 9.9 19.1L3.3 13.8C1.2 17.1 0 21.2 0 25C0 28.8 1.2 32.9 3.3 36.2L9.9 30.9Z" fill="#FBBC05" />
            <path d="M24 48C30.5 48 36.2 46.3 40.7 43.6L34.1 38.2C31.6 40.1 28.3 41.5 24 41.5C17.5 41.5 12.3 38.5 9.9 33.9L3.3 39.2C7.1 46.2 14.8 48 24 48Z" fill="#34A853" />
        </svg>
    )
}

const extractError = (err) => {
    const data = err.response?.data
    const status = err.response?.status
    const detail = data?.detail

    if (status === 401) {
        return typeof detail === 'string' ? detail : 'Incorrect email or password. Please try again.'
    }
    if (status === 400) {
        return typeof detail === 'string' ? detail : 'Account mapping configuration error.'
    }
    if (Array.isArray(detail)) {
        return detail.map((e) => e.msg.replace('Value error, ', '')).join('. ')
    }
    if (typeof detail === 'string') return detail
    if (!err.response) return 'Unable to reach the server. Please check your connection.'

    return 'Sign in failed. Please try again.'
}

export default function Login() {
    const { loginWithToken } = useAuth()
    const navigate = useNavigate()

    const [form, setForm] = useState({ email: '', password: '' })
    const [error, setError] = useState('')
    const [loading, setLoading] = useState(false)

    const isMounted = useRef(true)
    useEffect(() => {
        isMounted.current = true
        return () => { isMounted.current = false }
    }, [])

    const handleChange = (e) =>
        setForm((f) => ({ ...f, [e.target.name]: e.target.value }))

    const handleAuthStream = async (authPromise) => {
        setError('')
        setLoading(true)
        let createdToken = null

        try {
            const res = await authPromise
            createdToken = res.data.access_token

            localStorage.setItem('gmc_token', createdToken)

            const profileRes = await getProfile()
            loginWithToken(createdToken, profileRes.data)

            // Fade out then navigate
            const container = document.querySelector('.login-container')
            if (container) {
                container.style.transition = 'opacity 400ms ease-out'
                container.style.opacity = '0'
            }

            setTimeout(() => {
                navigate('/registration-success', {
                    state: {
                        userData: profileRes.data,
                        isNewUser: false  // ← Existing user logging in
                    }
                })
            }, 400)
        } catch (err) {
            if (createdToken) {
                localStorage.removeItem('gmc_token')
            }
            setError(extractError(err))
        } finally {
            if (isMounted.current) {
                setLoading(false)
            }
        }
    }

    const handleSubmit = (e) => {
        e.preventDefault()
        handleAuthStream(login(form))
    }

    const triggerGoogleSignIn = () => {
        const googleContainer = document.querySelector('#google-signin-container')
        if (googleContainer) {
            const button = googleContainer.querySelector('button') ||
                googleContainer.querySelector('[role="button"]')
            if (button) {
                button.click()
            }
        }
    }

    const handleGoogleSuccess = (credentialResponse) => {
        if (!credentialResponse.credential) {
            setError('Google Authentication payload transmission was empty.')
            return
        }
        handleAuthStream(googleLogin(credentialResponse.credential))
    }

    const handleGoogleFailure = () => {
        setError('Google Sign-In initialization failed or window was closed.')
    }

    return (
        <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
            <div className="min-h-screen bg-[#07090e] text-slate-100 font-body antialiased relative overflow-x-hidden flex flex-col selection:bg-brand-amber/30 selection:text-white">

                {/* Visual Depth Ambient Background Gradients */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[350px] bg-gradient-to-b from-indigo-500/5 to-transparent blur-[120px] pointer-events-none" />
                <div className="absolute bottom-0 left-[-10%] w-[500px] h-[500px] bg-brand-amber/[0.02] blur-[130px] pointer-events-none rounded-full" />

                <Navbar />

                <div className="flex-1 flex items-center justify-center px-6 py-20 md:py-28 relative z-10">
                    <div className="w-full max-w-[420px] login-container">

                        {/* Title Header Block */}
                        <div className="mb-10 text-center sm:text-left">
                            <h1 className="font-display text-4xl font-bold tracking-tight text-white mb-3">
                                Sign in
                            </h1>
                            <p className="text-slate-400 font-body text-sm font-light">
                                New to the risk engine?{' '}
                                <Link to="/register" className="text-brand-amber hover:text-amber-400 font-medium transition-colors ml-1">
                                    Create an account
                                </Link>
                            </p>
                        </div>

                        {/* ── GMC-Branded Google Sign-In Button (Dark Theme) ── */}
                        <div className="w-full mb-6">
                            <button
                                type="button"
                                onClick={triggerGoogleSignIn}
                                disabled={loading}
                                className="w-full h-12 bg-white/[0.03] hover:bg-white/[0.08] border border-white/[0.08] hover:border-white/[0.15] text-white rounded-lg font-sans font-medium text-sm flex items-center justify-center gap-3 transition-all duration-200 hover:shadow-lg hover:shadow-brand-amber/5 active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed group"
                            >
                                <span className="opacity-90 group-hover:opacity-100 transition-opacity">
                                    <GoogleIcon />
                                </span>
                                <span className="text-slate-300 group-hover:text-white transition-colors">
                                    Continue with Google
                                </span>
                            </button>

                            {/* Hidden GoogleLogin component */}
                            <div id="google-signin-container" className="hidden" style={{ display: 'none !important' }}>
                                <GoogleLogin
                                    onSuccess={handleGoogleSuccess}
                                    onError={handleGoogleFailure}
                                    useOneTap
                                    theme="filled_black"
                                    shape="pill"
                                    disabled={loading}
                                    width="400"
                                    text="continue_with"
                                    size="large"
                                />
                            </div>

                            <style dangerouslySetInnerHTML={{
                                __html: `
                                #google-signin-container {
                                    display: none !important;
                                    width: 0 !important;
                                    height: 0 !important;
                                    overflow: hidden !important;
                                    position: absolute !important;
                                    pointer-events: none !important;
                                    opacity: 0 !important;
                                }
                                #google-signin-container * {
                                    display: none !important;
                                    width: 0 !important;
                                    height: 0 !important;
                                }
                            ` }} />
                        </div>

                        {/* Elegant Divider Strip */}
                        <div className="mb-6">
                            <div className="flex items-center gap-4">
                                <div className="flex-1 h-px bg-white/[0.05]" />
                                <span className="text-slate-500 text-xs font-medium tracking-widest uppercase">or continue with email</span>
                                <div className="flex-1 h-px bg-white/[0.05]" />
                            </div>
                        </div>

                        {/* Auth Form */}
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="space-y-2">
                                <label className="block text-slate-300 text-xs font-medium tracking-wide uppercase">Email address</label>
                                <input
                                    name="email"
                                    type="email"
                                    placeholder="name@firm.com"
                                    value={form.email}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 bg-white/[0.02] focus:bg-white/[0.04] border border-white/[0.06] focus:border-brand-amber/40 rounded-lg text-white font-light placeholder-slate-600 focus:outline-none transition-all duration-200 shadow-[inset_0_1px_2px_rgba(255,255,255,0.01)] text-sm disabled:opacity-50"
                                    required
                                    autoComplete="email"
                                    disabled={loading}
                                />
                            </div>

                            <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                    <label className="block text-slate-300 text-xs font-medium tracking-wide uppercase">Password</label>
                                    <Link
                                        to="/forgot-password"
                                        className="text-xs font-body text-slate-400 hover:text-brand-amber transition-colors font-light"
                                    >
                                        Forgot password?
                                    </Link>
                                </div>
                                <input
                                    name="password"
                                    type="password"
                                    placeholder="••••••••••••"
                                    value={form.password}
                                    onChange={handleChange}
                                    className="w-full px-4 py-3 bg-white/[0.02] focus:bg-white/[0.04] border border-white/[0.06] focus:border-brand-amber/40 rounded-lg text-white font-light placeholder-slate-600 focus:outline-none transition-all duration-200 shadow-[inset_0_1px_2px_rgba(255,255,255,0.01)] text-sm disabled:opacity-50"
                                    required
                                    autoComplete="current-password"
                                    disabled={loading}
                                />
                            </div>

                            {/* Refined Error Panel */}
                            {error && (
                                <div className="border border-red-500/20 bg-red-500/[0.06] backdrop-blur-sm rounded-lg px-4 py-3.5 transition-all duration-300">
                                    <div className="flex gap-2.5 items-start">
                                        <div className="w-1.5 h-1.5 rounded-full bg-red-400 mt-1.5 shrink-0" />
                                        <p className="text-red-300/90 text-sm leading-normal font-light font-body">{error}</p>
                                    </div>
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full px-6 py-3.5 bg-white text-[#07090e] hover:bg-slate-200 disabled:bg-white/40 disabled:cursor-not-allowed font-medium rounded-lg transition-all duration-200 shadow-xl text-center text-sm"
                            >
                                {loading ? 'Verifying credentials…' : 'Sign in with email'}
                            </button>
                        </form>

                    </div>
                </div>
                <Footer />
            </div>
        </GoogleOAuthProvider>
    )
}